import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminHeader from '../../components/ui/AdminHeader';
import AdminClientSelector from '../../components/ui/AdminClientSelector';
import WelcomeSection from './components/WelcomeSection';
import QuickStatsGrid from './components/QuickStatsGrid';
import QuickActionsPanel from './components/QuickActionsPanel';
import RecentActivityFeed from './components/RecentActivityFeed';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, userRole, userProfile, loading } = useAuth();

  // Check authentication and role on mount
  useEffect(() => {
    if (!loading) {
      // If not logged in at all, redirect to admin login
      if (!user) {
        navigate('/admin-login');
        return;
      }

      // If logged in but not admin or mover, redirect to appropriate page
      if (userRole !== 'admin' && userRole !== 'mover') {
        // If they're a customer, send them to client dashboard
        if (userRole === 'customer') {
          navigate('/client-dashboard');
        } else {
          // Otherwise send them back to admin login
          navigate('/admin-login');
        }
      }
    }
  }, [user, userRole, loading, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  // Don't render dashboard if not admin or mover
  if (!user || (userRole !== 'admin' && userRole !== 'mover')) {
    return null;
  }

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    const storedClient = localStorage.getItem('selectedClient');
    if (storedClient && storedClient !== 'undefined') {
      try {
        setSelectedClient(JSON.parse(storedClient));
      } catch (e) {
        console.error('Failed to parse selectedClient from localStorage', e);
        localStorage.removeItem('selectedClient');
      }
    }
  }, []);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    localStorage.setItem('selectedClient', JSON.stringify(client));
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />

      <div className="flex pt-16">
        <AdminClientSelector
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onClientSelect={handleClientSelect}
        />

        <main className="flex-1 p-4 lg:p-6 overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 lg:hidden">
              <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
              <Button
                variant="outline"
                size="icon"
                onClick={toggleSidebar}
                iconName="Menu"
                iconSize={20}
              >
                <span className="sr-only">Toggle client selector</span>
              </Button>
            </div>

            <WelcomeSection selectedClient={selectedClient} />

            <QuickStatsGrid
              selectedClient={selectedClient}
              onTotalClientsClick={toggleSidebar}
            />

            <QuickActionsPanel selectedClient={selectedClient} />

            <RecentActivityFeed />

            <div className="mt-6 p-6 bg-card rounded-xl border border-border">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="HelpCircle" size={24} color="var(--color-primary)" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Need Help?
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Contact support for assistance with inventory management.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="MessageCircle"
                      iconPosition="left"
                      onClick={() => window.location.href = 'mailto:MGDockery@me.com?subject=Emergency on Erwin\'s LaSea Inventory Portal'}
                    >
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}